import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/i18n";
import { scrollToId } from "@/lib/scroll";

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const mediaY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section className="hero" id="top" ref={ref} aria-labelledby="hero-title" data-testid="hero-section">
      <motion.div
        className="hero-media"
        style={{ y: mediaY, backgroundImage: "url(/assets/tech-training-hero.webp)" }}
        aria-hidden="true"
      />
      <div className="hero-grid" aria-hidden="true" />
      <div className="hero-content shell">
        <motion.div className="hero-copy" style={{ opacity: fade }}>
          <motion.p
            className="eyebrow"
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            data-testid="hero-eyebrow"
          >
            {t.hero.eyebrow}
          </motion.p>
          <h1 id="hero-title" data-testid="hero-title">
            {t.hero.lines.map((line, i) => (
              <span className="line-mask" key={`${line}-${i}`}>
                <motion.span
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.3 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                >
                  {i === 1 ? <em>{line}</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            className="hero-lead"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            data-testid="hero-lead"
          >
            {t.hero.lead}
          </motion.p>
          <motion.div
            className="hero-actions"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            <button className="button button-primary" onClick={() => scrollToId("#metodo")} data-testid="hero-primary-cta">
              {t.hero.primary} <span aria-hidden="true">↓</span>
            </button>
            <button className="button button-ghost" onClick={() => scrollToId("#professionisti")} data-testid="hero-secondary-cta">
              {t.hero.secondary}
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          className="hero-status"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          aria-label={t.hero.statusTag}
          data-testid="hero-status"
        >
          <div className="status-dot">
            <span></span> {t.hero.statusTag}
          </div>
          <div className="status-number">{t.hero.statusNum}</div>
          <p>{t.hero.statusText}</p>
        </motion.div>
      </div>

      <motion.div
        className="hero-bottom shell"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.3 }}
        aria-label={t.hero.bottom.join(", ")}
        data-testid="hero-bottom"
      >
        {t.hero.bottom.map((b) => (
          <span key={b}>{b}</span>
        ))}
      </motion.div>
    </section>
  );
};

export default Hero;
